import React, { useEffect, useState, Fragment } from "react";
import SecondLayout from "../layouts/SecondLayout";
import Cookies from "js-cookie";
import { useNavigate, useParams } from "react-router-dom";
import { Variables } from "../variables";
import Rejuvenation from "./Rejuvenation";
import Skincare from "./Skincare";
import HealthWellness from "./HealthWellness";
import Cosmetic from "./Cosmetic";
import VideoSection from "../components/partials/RejuvenationDetail/VideoSection";
import WhyRejuvenation from "../components/partials/RejuvenationDetail/WhyRejuvenation";

const CategoryDetail = () => {
  const navigate = useNavigate();
  const { name, id } = useParams();
  const categoryId = Number(id) || 0;

  const [language, setLanguage] = useState(Cookies.get("RashaLang") || "ar");
  const [content, setContent] = useState([]);

  // loader + error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // watch language cookie
  useEffect(() => {
    const interval = setInterval(() => {
      const currentLang = Cookies.get("RashaLang") || "ar";
      setLanguage((prevLang) =>
        prevLang !== currentLang ? currentLang : prevLang
      );
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // fetch data (show ONLY loader/error without layout; show layout after)
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = Variables.API_URL + "Rasha/Client/Category/" + categoryId;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.message || "Failed to fetch content");
        }

        if (isMounted) setContent(Array.isArray(result) ? result : []);
      } catch (err) {
        if (isMounted && err.name !== "AbortError") {
          setError(err.message || "Something went wrong");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [categoryId]);

  // --- Loader (no layout) ---
  if (loading) {
    return (
      <div style={styles.loaderWrap}>
        <div style={styles.spinner} />
      </div>
    );
  }

  // --- Error (no layout) ---
  if (error) {
    return <div style={styles.errorBox}>{error}</div>;
  }

  // --- Main UI (with layout AFTER loading) ---
  return (
    <SecondLayout>
      {categoryId === 1 ? (
        <Rejuvenation />
      ) : categoryId === 2 ? (
        <Skincare />
      ) : categoryId === 3 ? (
        <HealthWellness />
      ) : (
        <Cosmetic />
      )}

      <div className="container">
        <div dir={language === "ar" ? "rtl" : "ltr"}>
          <div className="product-grid">
            {content.map((c, index) => {
              const key = c?.Id ?? c?.WidgetId ?? index;
              const img = Variables.API_PUBLISHED + (c?.ImageUrl || "");
              const title = language === "en" ? c?.Name : c?.NameAr;
              const body = language === "en" ? c?.Body : c?.BodyAr;
              const btnText =
                language === "ar" ? c?.ButtonTextAr : c?.ButtonText;
              const btnUrl = c?.ButtonUrl || "#";

              return (
                <div className="product-card-skincare" key={key}>
                  <img
                    src={img}
                    alt={title || "item"}
                    className="product-img"
                  />
                  <h3 className="product-title">{title}</h3>
                  <div className="text-content-body">
                    {body &&
                      body
                        .split(".")
                        .filter((s) => s.trim() !== "")
                        .map((sentence, idx, arr) => {
                          const isLast = idx === arr.length - 1;
                          const trimmed = sentence.trim();
                          const endsWithBuy =
                            trimmed
                              .toLowerCase()
                              .includes("you can also buy") ||
                            trimmed.toLowerCase().includes("you can buy");

                          return (
                            <p key={idx} className="product-desc">
                              {isLast && endsWithBuy ? (
                                <strong>{trimmed}.</strong>
                              ) : (
                                `${trimmed}.`
                              )}
                            </p>
                          );
                        })}
                  </div>
                  <button
                    className="order-btn"
                    onClick={() => navigate(`${btnUrl}?category=${name}&Name=${c?.Name}`)}
                  >
                    {btnText}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {categoryId === 1 ? (
        <Fragment>
          <WhyRejuvenation language={language} />
          <VideoSection
            videoUrl={
              "https://www.youtube.com/embed/dHqeI-LiRao?autoplay=1&mute=1&playsinline=1&rel=0&showinfo=0&controls=1"
            }
          />
        </Fragment>
      ) : categoryId === 2 ? (
        <VideoSection
          videoUrl={
            "https://www.youtube.com/embed/WjQjRlBQjkI?autoplay=1&mute=1&playsinline=1&rel=0&showinfo=0&controls=1"
          }
        />
      ) : categoryId === 3 ? null : (
        <div className="mt-5">
          <VideoSection
            videoUrl={
              "https://www.youtube.com/embed/WXwUTLVYV8c?autoplay=1&mute=1&playsinline=1&rel=0&showinfo=0&controls=1"
            }
          />
        </div>
      )}
    </SecondLayout>
  );
};

const styles = {
  loaderWrap: {
    minHeight: "60vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    width: 48,
    height: 48,
    border: "4px solid rgba(0,0,0,0.1)",
    borderTopColor: "#000",
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  },
  errorBox: {
    margin: 16,
    padding: "12px 16px",
    border: "1px solid #f3c2c2",
    background: "#fff5f5",
    color: "#b00020",
    borderRadius: 8,
  },
};

// inject keyframes (once, unique id for this page)
if (
  typeof document !== "undefined" &&
  !document.getElementById("categorydetail-spin-kf")
) {
  const styleEl = document.createElement("style");
  styleEl.id = "categorydetail-spin-kf";
  styleEl.innerHTML = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleEl);
}

export default CategoryDetail;
